import { isValidElement, ReactNode } from 'react';
import { MessageDescriptor } from '@lingui/core';
import { useLingui } from '@lingui/react/macro';

import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconInfo from '@/icons/Info.svg';
import { useGetArticleName } from '@/models/article';
import { ProductAttributeValueWithParent } from '@/models/request';
import { I18nWording } from '@/services/i18n';
import { createBEMClasses } from '@/utils/classname';
import { formatDate } from '@/utils/date';
import { formatList } from '@/utils/list';

import './BaseActivity.css';

const { block, element } = createBEMClasses('activity');

export const BaseActivity = ({
  createdAt,
  message,
  icon = <IconInfo />,
  type = 'neutral',
  children,
}: {
  createdAt: string;
  message: ReactNode | MessageDescriptor;
  icon?: ReactNode;
  type?: 'neutral' | 'primary' | 'secondary' | 'warning' | 'danger';
  children?: ReactNode;
}) => {
  const { i18n } = useLingui();

  return (
    <Stack className={block()}>
      <div className={element('line')}></div>
      <Stack className={element('content')}>
        <div className={element('icon', { type })}>{icon}</div>
        <div>
          {isValidElement(message) ? (
            message
          ) : (
            <BaseActivityText>{i18n._(message as MessageDescriptor)}</BaseActivityText>
          )}
          <InlineText
            color="disabled"
            title={formatDate(new Date(createdAt), { dateStyle: 'full', timeStyle: 'short' })}
          >
            {' '}
            • {formatDate(new Date(createdAt), { timeStyle: 'short' })}
          </InlineText>
        </div>
      </Stack>
      <Stack row className={element('detailed-content')}>
        {children}
      </Stack>
    </Stack>
  );
};
export const BaseActivityText = ({ children }: { children: ReactNode }) => {
  return <InlineText color="secondary">{children}</InlineText>;
};

export const BaseActivityDetailedContent = ({ children }: { children: ReactNode }) => {
  return <div className="activity__comment-container">{<Paragraph>{children}</Paragraph>}</div>;
};

type ActivityWithArticle = {
  article: {
    product: {
      name: string;
    } | null;
    productAttributeValues: ProductAttributeValueWithParent[];
    color: I18nWording | null;
  };
};

export const BaseActivityArticlesNames = ({
  activities,
  displayArticleName,
}: {
  activities: ActivityWithArticle[];
  displayArticleName: boolean;
}) => {
  const getArticleName = useGetArticleName();
  const articleNames = activities.map((activity) => getArticleName({ article: activity.article }));

  return displayArticleName ? (
    <InlineText weight="medium"> {formatList(articleNames.sort())} </InlineText>
  ) : null;
};

export const BaseActivityArticleName = ({
  activity,
  displayArticleName,
}: {
  activity: ActivityWithArticle;
  displayArticleName: boolean;
}) => {
  return (
    <BaseActivityArticlesNames activities={[activity]} displayArticleName={displayArticleName} />
  );
};
